import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { DASHBOARD_CATEGORIES } from '../data/dashboardCategories';
import './allcategories.css';

const CategoryCard = ({ category, isComingSoon = false }) => {
  const cardContent = (
    <div className={`category-card ${isComingSoon ? 'coming-soon' : ''}`}>
      <div className="category-icon">
        {category.icon ? (
          <img src={category.icon} alt={category.name} />
        ) : (
          <div className="fallback-icon">📁</div>
        )}
      </div>
      <h3 className="category-name">{category.name}</h3>
      <p className="category-count">
        {isComingSoon ? 'Coming Soon' : 'Available'}
      </p>
    </div>
  );

  if (isComingSoon) {
    return <div className="category-card-wrapper disabled">{cardContent}</div>;
  }

  return (
    <Link to={`/category/${category.slug}`} className="category-card-wrapper">
      {cardContent}
    </Link>
  );
};

CategoryCard.propTypes = {
  category: PropTypes.shape({
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    enabled: PropTypes.bool
  }).isRequired,
  isComingSoon: PropTypes.bool
};

const CategorySection = ({ title, icon, categories, showComingSoonBadge = false }) => {
  if (!categories || categories.length === 0) return null;

  return (
    <section className="category-section">
      <div className="section-header">
        <h2 className="section-title">
          {icon && <span className="section-icon">{icon}</span>}
          {title}
        </h2>
        {showComingSoonBadge && (
          <span className="coming-soon-badge">Coming Soon</span>
        )}
      </div>
      <div className="categories-grid">
        {categories.map((category) => (
          <CategoryCard 
            key={category.slug} 
            category={category} 
            isComingSoon={showComingSoonBadge}
          />
        ))}
      </div>
    </section>
  );
};

CategorySection.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      enabled: PropTypes.bool
    })
  ).isRequired,
  showComingSoonBadge: PropTypes.bool
};

const AllCategories = () => {
  // Process DASHBOARD_CATEGORIES to match the expected structure
  const enabledCategories = React.useMemo(
    () => DASHBOARD_CATEGORIES.filter(category => category.enabled),
    []
  );

  // Separate popular categories (featured or high count) and regular categories
  const { popularCategories, allCategories } = React.useMemo(() => {
    // For demo purposes, mark first few as popular
    const popular = enabledCategories.slice(0, 6);
    
    return {
      popularCategories: popular,
      allCategories: enabledCategories
    };
  }, [enabledCategories]);

  return (
    <div className="all-categories-page">
      <div className="page-header">
        <h1 className="page-title">All Categories</h1>
        <p className="page-subtitle">
          Discover businesses across all categories. Find exactly what you&apos;re looking for.
        </p>
      </div>

      <div className="categories-container">
        {/* Popular Categories */}
        <CategorySection 
          title="Popular Categories" 
          icon="⭐" 
          categories={popularCategories.slice(0, 8)} // Show top 8 popular categories
        />

        {/* All Categories */}
        <CategorySection 
          title="All Categories" 
          icon="📂" 
          categories={allCategories}
        />
      </div>
    </div>
  );
};

// PropTypes definitions
CategoryCard.propTypes = {
  category: PropTypes.shape({
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    enabled: PropTypes.bool
  }).isRequired,
  isComingSoon: PropTypes.bool
};

CategorySection.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      enabled: PropTypes.bool
    })
  ).isRequired,
  showComingSoonBadge: PropTypes.bool
};

export default AllCategories;
